import { Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OtpVerify } from "@/components/site/OtpVerify";

export const STEPS = ["Select job", "Personal details", "Education", "Documents", "Review", "Fee & payment"];

export const Stepper = ({ step }) => (
  <ol className="mb-8 grid grid-cols-6 gap-1.5" data-testid="apply-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="space-y-1.5">
        <div className={`h-1.5 rounded-full ${i + 1 <= step ? "bg-amber-600" : "bg-slate-200"}`} />
        <p className={`hidden text-xs font-semibold sm:block ${i + 1 === step ? "text-slate-900" : "text-slate-400"}`}>{i + 1}. {s}</p>
      </li>
    ))}
  </ol>
);

export const Field = ({ id, label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

export const StepJob = ({ jobs, jobId, setJobId, locked }) => (
  <div className="space-y-3">
    {jobs.map((j) => {
      const sel = j.id === jobId;
      const open = j.applications_open && (!j.deadline || new Date(j.deadline + "T23:59:59") >= new Date());
      return (
        <button key={j.id} type="button" disabled={locked || !open} onClick={() => setJobId(j.id)} className={`flex w-full items-center justify-between gap-4 rounded-2xl border-2 p-5 text-left transition-colors ${sel ? "border-amber-600 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-400"} disabled:cursor-not-allowed disabled:opacity-60`} data-testid={`select-job-${j.id}`}>
          <div>
            <p className="font-display text-lg font-bold">{j.title}</p>
            <p className="text-sm text-slate-600"><span className="font-mono-num">{j.salary_text}</span> / month · {j.vacancies.toLocaleString("en-IN")} vacancies · {j.incentives}</p>
            {!open && <p className="text-xs font-semibold text-red-600">Applications closed</p>}
          </div>
          <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 ${sel ? "border-amber-600 bg-amber-600 text-white" : "border-slate-300"}`}>{sel && <Check className="h-3.5 w-3.5" />}</span>
        </button>
      );
    })}
  </div>
);

const P_FIELDS = [
  ["full_name", "Full name"], ["father_name", "Father's name"], ["mother_name", "Mother's name"], ["dob", "Date of birth", "date"],
];
const A_FIELDS = [["state", "State"], ["district", "District"], ["pincode", "PIN code"]];

export const StepPersonal = ({ p, setP, emailLocked, emailVerified, onEmailToken, sms, mobileVerified, onMobileToken }) => {
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {P_FIELDS.map(([k, l, t]) => (
        <Field key={k} id={k} label={`${l} *`}><Input id={k} type={t || "text"} value={p[k]} onChange={set(k)} data-testid={`personal-${k}-input`} /></Field>
      ))}
      <Field id="gender" label="Gender (optional)">
        <Select value={p.gender || ""} onValueChange={(v) => setP({ ...p, gender: v })}>
          <SelectTrigger id="gender" data-testid="personal-gender-select"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
          <SelectContent>{["Female", "Male", "Other", "Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div />
      <Field id="email" label="Email address *" className="sm:col-span-2">
        <Input id="email" type="email" value={p.email} disabled={emailLocked} onChange={set("email")} data-testid="personal-email-input" />
        <OtpVerify channel="email" target={p.email} verified={emailVerified} onVerified={onEmailToken} testid="email-otp" />
      </Field>
      <Field id="mobile" label="Mobile number *" className="sm:col-span-2">
        <Input id="mobile" inputMode="numeric" maxLength={10} value={p.mobile} onChange={(e) => setP({ ...p, mobile: e.target.value.replace(/\D/g, "") })} data-testid="personal-mobile-input" />
        {sms && <OtpVerify channel="sms" target={p.mobile} verified={mobileVerified} onVerified={onMobileToken} testid="mobile-otp" />}
      </Field>
      <Field id="address" label="Full residential address *" className="sm:col-span-2"><Textarea id="address" value={p.address} onChange={set("address")} data-testid="personal-address-input" /></Field>
      {A_FIELDS.map(([k, l]) => (
        <Field key={k} id={k} label={`${l} *`}><Input id={k} value={p[k]} onChange={set(k)} data-testid={`personal-${k}-input`} /></Field>
      ))}
    </div>
  );
};

export const StepEducation = ({ e, setE, qualifications, job }) => {
  const set = (k) => (ev) => setE({ ...e, [k]: ev.target.value });
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <p className="rounded-xl border-l-4 border-amber-600 bg-amber-50 p-3 text-sm font-semibold text-amber-900 sm:col-span-2" data-testid="education-eligibility-notice">You can apply for any post only if you have passed secondary school (10th Pass).</p>
      {job?.min_qualification && <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 sm:col-span-2" data-testid="job-requirement">Requirement for {job.title}: minimum <strong>{job.min_qualification}</strong>{job.min_age ? `, age ${job.min_age}–${job.max_age || "—"} years` : ""}. {job.qualifications}</p>}
      <Field id="hq" label="Highest qualification *">
        <Select value={e.highest_qualification} onValueChange={(v) => setE({ ...e, highest_qualification: v })}>
          <SelectTrigger id="hq" data-testid="education-qualification-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{qualifications.map((q) => <SelectItem key={q} value={q} data-testid={`qualification-option-${q}`}>{q}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field id="year" label="Passing year *"><Input id="year" inputMode="numeric" maxLength={4} value={e.passing_year} onChange={set("passing_year")} data-testid="education-year-input" /></Field>
      <Field id="inst" label="School / institution name *" className="sm:col-span-2"><Input id="inst" value={e.institution} onChange={set("institution")} data-testid="education-institution-input" /></Field>
      <Field id="addq" label="Additional qualifications" className="sm:col-span-2"><Textarea id="addq" value={e.additional_qualifications} onChange={set("additional_qualifications")} data-testid="education-additional-input" /></Field>
      <Field id="exp" label="Relevant work experience" className="sm:col-span-2"><Textarea id="exp" value={e.work_experience} onChange={set("work_experience")} data-testid="education-experience-input" /></Field>
      <Field id="skills" label="Skills and professional experience" className="sm:col-span-2"><Textarea id="skills" value={e.skills} onChange={set("skills")} data-testid="education-skills-input" /></Field>
    </div>
  );
};
